import LiquidText from '../components/LiquidText';
import { OrcamentoIcon } from '../components/OrcamentoUI';
import { useLiquidFeedback, LiquidDialog } from '../components/LiquidUI';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import ObraRecursos from '../components/ObraRecursos';
import MapSearch from '../components/MapSearch';
import FichasLevantamentoLista from '../components/FichasLevantamentoLista';
import './ObraDetalhes.css';

const ObraDetalhes = () => {
    const liquidFeedback = useLiquidFeedback();
    const { id } = useParams();
    const navigate = useNavigate();
    const [obra, setObra] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generatingDetailed, setGeneratingDetailed] = useState(false);
    const [generatingSimple, setGeneratingSimple] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [savingMap, setSavingMap] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState(null);
    const [availableWorkItems, setAvailableWorkItems] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const mainMarkerRef = useRef(null);
    const [users, setUsers] = useState([]);
    const [clientes, setClientes] = useState([]);

    // Map Drawing State (same as NovaObra)
    const [mapTools, setMapTools] = useState({
        activeTool: null,
        polygons: { red: [], yellow: [] },
        icons: [],
        drawnLayers: []
    });
    const [currentPolygonPoints, setCurrentPolygonPoints] = useState([]);

    // Função para fixar o mapa atual como imagem para o PDF
    const handleFixMap = async () => {
        if (!mapRef.current) return;
        setSavingMap(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 800));
            const canvas = await (await import('html2canvas')).default(mapRef.current, {
                useCORS: true,
                allowTaint: false,
                backgroundColor: 'white',
                scale: 2,
                logging: false
                // Polígonos serão capturados via preferCanvas
            });
            const base64 = canvas.toDataURL('image/jpeg', 0.8);
            await api.patch(`/obras/${id}/map-image`, { mapa_imagem: base64 });
            setObra(prev => ({ ...prev, mapa_imagem: base64 }));
            liquidFeedback.notify('✅ Mapa fixado com sucesso! Esta vista será usada nos próximos relatórios.');
        } catch (err) {
            console.error('Erro ao fixar mapa:', err);
            liquidFeedback.notify('Erro ao capturar mapa. Tente novamente.');
        } finally {
            setSavingMap(false);
        }
    };

    // Maquete Generation
    const [showMaqueteModal, setShowMaqueteModal] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplateForGen, setSelectedTemplateForGen] = useState('');
    const [selectedWorkItemToAdd, setSelectedWorkItemToAdd] = useState(''); // Estado para o dropdown de adicionar

    const handleOpenMaqueteEditor = () => {
        navigate(`/maquetes/${id}`);
    };

    const handleGenerateMaquete = async () => {
        if (!selectedTemplateForGen) return;
        try {
            const response = await api.get(`/maquetes/generate/${selectedTemplateForGen}/obra/${id}`, {
                responseType: 'blob'
            });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `maquete_${obra?.posicao || 'obra'}.pdf`;
            link.click();
            window.URL.revokeObjectURL(url);
            setShowMaqueteModal(false);
        } catch (error) {
            console.error('Erro ao gerar maquete:', error);
            liquidFeedback.notify('Erro ao gerar maquete');
        }
    };

    // Ferramentas registadas que não estão nesta obra não entram no documento de
    // segurança. Avisa antes de o gerar ou enviar: a lista da obra fica no fundo
    // da página, longe destes botões.
    const avisoFerramentasEmFalta = async () => {
        try {
            const r = await api.get(`/obra-recursos/${id}/equipamentos/em-falta`);
            const lista = r.data?.em_falta || [];
            if (!lista.length) return '';
            const nomes = lista.slice(0, 5).map(f => `• ${f.nome}`).join('\n')
                + (lista.length > 5 ? `\n• … e mais ${lista.length - 5}` : '');
            return `${lista.length === 1 ? '1 ferramenta registada não está' : `${lista.length} ferramentas registadas não estão`} nesta obra e não vão aparecer no documento de segurança:\n${nomes}`;
        } catch {
            return ''; // o aviso é uma ajuda: não impede o documento
        }
    };
    const irParaRecursos = () => document.getElementById('obra-recursos')?.scrollIntoView({ behavior: 'smooth' });

    // Função para gerar o Relatório PDF Completo com Mapa
    const handleGeneratePDF = async () => {
        setGeneratingDetailed(true);
        try {
            const aviso = await avisoFerramentasEmFalta();
            if (aviso && !await liquidFeedback.confirm(`${aviso}\n\nOK — gerar o PDF sem elas.\nCancelar — voltar para as adicionar (Equipamentos, no fundo da página).`)) {
                irParaRecursos();
                return;
            }

            // Usar mapa fixado se existir, senão capturar no momento
            let mapImage = obra.mapa_imagem;
            if (!mapImage && mapRef.current) {
                const canvas = await (await import('html2canvas')).default(mapRef.current, {
                    useCORS: true,
                    allowTaint: false,
                    backgroundColor: 'white',
                    scale: 1.5
                });
                mapImage = canvas.toDataURL('image/jpeg', 0.8);
            }

            const response = await api.post(`/export/obra/${id}/documentos-seguranca`, {
                mapImage: mapImage
            }, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `documentos_seguranca_${obra?.posicao || 'obra'}.pdf`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Erro ao gerar PDF:', error);
            liquidFeedback.notify('Erro ao gerar PDF: ' + (error.response?.data?.error || error.message));
        } finally {
            setGeneratingDetailed(false);
        }
    };

    const [sendingEmail, setSendingEmail] = useState(false);
    const handleSendEmail = async () => {
        // Obter emails de segurança e fiscalização para mostrar na confirmação
        let targetEmail = null;
        if (obra.seguranca_obra_id) {
            const segurancaUser = users.find(u => u.id === obra.seguranca_obra_id);
            if (segurancaUser?.email) targetEmail = segurancaUser.email;
        }
        const fiscalizacaoEmail = obra.email_fiscalizacao || null;

        const destinatarios = [targetEmail, fiscalizacaoEmail].filter(Boolean);
        const destStr = destinatarios.length > 0 ? destinatarios.join(' e ') : 'email interno';
        const aviso = await avisoFerramentasEmFalta();
        if (!await liquidFeedback.confirm(`Tem a certeza que deseja enviar o relatório por email?\n\nDestinatários: ${destStr}`
            + (aviso ? `\n\n⚠️ ${aviso}` : ''))) {
            if (aviso) irParaRecursos();
            return;
        }

        setSendingEmail(true);
        try {
            let mapImage = obra.mapa_imagem;
            if (!mapImage && mapRef.current) {
                const canvas = await (await import('html2canvas')).default(mapRef.current, {
                    useCORS: true,
                    allowTaint: false,
                    backgroundColor: 'white',
                    scale: 1.5
                });
                mapImage = canvas.toDataURL('image/jpeg', 0.8);
            }

            await api.post(`/export/obra/${id}/email-documentos-seguranca`, {
                mapImage: mapImage,
                targetEmail: targetEmail,
                fiscalizacaoEmail: fiscalizacaoEmail,
            });

            // Atualizar estado local após envio com sucesso
            setObra(prev => ({ ...prev, email_seguranca_enviado: true }));
            if (editData) setEditData(prev => ({ ...prev, email_seguranca_enviado: true }));

            liquidFeedback.notify(`✅ Email enviado com sucesso!\nDestinatários: ${destStr}`);
        } catch (error) {
            console.error('Erro ao enviar email:', error);
            liquidFeedback.notify('Erro ao enviar email: ' + (error.response?.data?.error || error.message));
        } finally {
            setSendingEmail(false);
        }
    };

    const handleDeleteObra = async () => {
        if (!await liquidFeedback.confirm(`⚠️ Atenção: Tem a certeza que deseja eliminar DEFINITIVAMENTE a obra ${obra.posicao} - ${obra.cliente}?\n\nEsta ação não pode ser revertida.`)) {
            return;
        }

        setIsDeleting(true);
        try {
            await api.delete(`/obras/${id}`);
            liquidFeedback.notify('✅ Obra eliminada com sucesso.');
            navigate('/obras');
        } catch (err) {
            console.error('Erro ao eliminar obra:', err);
            liquidFeedback.notify('Erro ao eliminar obra. Verifique se existem dependências ou tente novamente.');
        } finally {
            setIsDeleting(false);
        }
    };

    useEffect(() => {
        const fetchObra = async () => {
            try {
                const res = await api.get(`/obras/${id}`);
                const obraData = res.data.obra || res.data;
                setObra(obraData);
                setEditData({
                    posicao: obraData.posicao || '',
                    descricao: obraData.descricao || '',
                    cliente: obraData.cliente || '',
                    cliente_id: obraData.cliente_id || '',
                    data_inicio: obraData.data_inicio ? obraData.data_inicio.split('T')[0] : '',
                    data_fim: obraData.data_fim ? obraData.data_fim.split('T')[0] : '',
                    status: obraData.status || 'planeada',
                    gestor_cliente: obraData.gestor_cliente || '',
                    responsavel: obraData.responsavel || '',
                    documentacao_validada: obraData.documentacao_validada || '',
                    necessidade_at: obraData.necessidade_at || '',
                    data_at: obraData.data_at ? obraData.data_at.split('T')[0] : '',
                    hora_at: obraData.hora_at || '',
                    tipo_at: obraData.tipo_at || '',
                    observacoes: obraData.observacoes || '',
                    detalhes_trabalho: obraData.detalhes_trabalho || [],
                    tipo_trabalho: obraData.tipo_trabalho || [],
                    latitude: obraData.latitude,
                    longitude: obraData.longitude,
                    map_data: obraData.map_data,
                    responsavel_cliente_id: obraData.responsavel_cliente_id || '',
                    email_responsavel_cliente: obraData.email_responsavel_cliente || '',
                    telefone_responsavel_cliente: obraData.telefone_responsavel_cliente || '',
                    responsavel_obra_id: obraData.responsavel_obra_id || '',
                    email_responsavel: obraData.email_responsavel || '',
                    telefone_responsavel: obraData.telefone_responsavel || '',
                    seguranca_obra_id: obraData.seguranca_obra_id || '',
                    email_seguranca: obraData.email_seguranca || '',
                    telefone_seguranca: obraData.telefone_seguranca || '',
                    nome_fiscalizacao: obraData.nome_fiscalizacao || '',
                    email_fiscalizacao: obraData.email_fiscalizacao || '',
                    telefone_fiscalizacao: obraData.telefone_fiscalizacao || '',
                    fiscalizacao_id: obraData.fiscalizacao_id || '',
                    gestor_cliente_id: obraData.gestor_cliente_id || '',
                    email_seguranca_enviado: obraData.email_seguranca_enviado || false,
                    tipo_obra: obraData.tipo_obra || 'obra'
                });
            } catch (error) {
                console.error('Erro ao carregar obra:', error);
                liquidFeedback.notify('Erro ao carregar detalhes da obra');
            } finally {
                setLoading(false);
            }
        };

        const fetchWorkItems = async () => {
            try {
                const res = await api.get('/config/trabalhos');
                setAvailableWorkItems(res.data);
            } catch (err) {
                console.error('Error loading work items:', err);
            }
        };

        const fetchUsers = async () => {
            try {
                const res = await api.get('/auth/users');
                setUsers(res.data.users || res.data);
            } catch (err) {
                console.error('Error loading users:', err);
            }
        };

        fetchObra();
        fetchWorkItems();
        fetchUsers();
        api.get('/clientes').then(r => setClientes(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }, [id]);

    const handleLocationSelect = (loc) => {
        if (mapInstanceRef.current && mainMarkerRef.current) {
            mapInstanceRef.current.flyTo([loc.lat, loc.lng], 18);
            mainMarkerRef.current.setLatLng([loc.lat, loc.lng]);
            setEditData(prev => ({
                ...prev,
                latitude: loc.lat,
                longitude: loc.lng
            }));
        }
    };

    const handleSaveEdit = async () => {
        setIsSaving(true);
        try {
            // Garantir que não enviamos 0,0 se já tínhamos coordenadas válidas
            const finalLat = (editData.latitude && editData.latitude !== 0) ? editData.latitude : (obra.latitude || 0);
            const finalLng = (editData.longitude && editData.longitude !== 0) ? editData.longitude : (obra.longitude || 0);
            // Usar mapTools como fonte de verdade para os desenhos do mapa
            const finalMapData = {
                polygons: mapTools.polygons,
                icons: mapTools.icons
            };

            const payload = {
                ...editData,
                latitude: finalLat,
                longitude: finalLng,
                map_data: finalMapData
            };

            const res = await api.put(`/obras/${id}`, payload);
            const updatedObra = res.data.obra || res.data;

            setObra(updatedObra);
            setIsEditing(false);

            // Forçar re-inicialização do mapa se as coordenadas mudaram ou se o mapa estava "preto"
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }

            liquidFeedback.notify('✅ Alterações guardadas com sucesso!');
        } catch (error) {
            console.error('Erro ao salvar:', error);
            liquidFeedback.notify('Erro ao salvar alterações');
        } finally {
            setIsSaving(false);
        }
    };

    // Funcionalidades de Gestão de Trabalhos (Adicionar, Remover, Importar)
    const handleAddWorkItem = () => {
        if (!selectedWorkItemToAdd) return;

        const item = availableWorkItems.find(i => i.id === parseInt(selectedWorkItemToAdd));
        if (!item) return;

        setEditData(prev => {
            // Verificar se já existe
            if (prev.detalhes_trabalho.find(d => d.id === item.id)) {
                liquidFeedback.notify('Este trabalho já está na lista.');
                return prev;
            }

            const newDetalhes = [...prev.detalhes_trabalho, { ...item, quantidade: 1 }];
            return {
                ...prev,
                detalhes_trabalho: newDetalhes,
                tipo_trabalho: newDetalhes.map(d => d.nome)
            };
        });
        setSelectedWorkItemToAdd('');
    };

    const handleRemoveWorkItem = (itemId) => {
        setEditData(prev => {
            const newDetalhes = prev.detalhes_trabalho.filter(d => d.id !== itemId);
            return {
                ...prev,
                detalhes_trabalho: newDetalhes,
                tipo_trabalho: newDetalhes.map(d => d.nome)
            };
        });
    };

    const handleImportProjectExcel = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const L = (await import('xlsx')).default;
            const reader = new FileReader();
            reader.onload = (evt) => {
                try {
                    const bstr = evt.target.result;
                    const wb = L.read(bstr, { type: 'binary' });
                    const wsname = wb.SheetNames[0];
                    const ws = wb.Sheets[wsname];
                    const data = L.utils.sheet_to_json(ws);

                    // Copiar itens existentes para não perder
                    const currentItems = [...editData.detalhes_trabalho];

                    data.forEach(row => {
                        const nome = row['Nome'] || row['nome'] || row['Trabalho'] || row['trabalho'];
                        const qtd = parseInt(row['Quantidade'] || row['quantidade'] || row['Qtd'] || 1);

                        if (nome) {
                            const item = availableWorkItems.find(i => i.nome.toLowerCase() === nome.toLowerCase());
                            if (item) {
                                // Verificar se já está na lista atual ou na nova
                                const existingIndex = currentItems.findIndex(ci => ci.id === item.id);
                                if (existingIndex >= 0) {
                                    // Atualizar quantidade
                                    currentItems[existingIndex].quantidade = qtd;
                                } else {
                                    // Adicionar novo
                                    currentItems.push({ ...item, quantidade: qtd });
                                }
                            }
                        }
                    });

                    setEditData(prev => ({
                        ...prev,
                        detalhes_trabalho: currentItems,
                        tipo_trabalho: currentItems.map(d => d.nome)
                    }));
                    liquidFeedback.notify(`Importação concluída! Lista de trabalhos atualizada.`);
                } catch (parseError) {
                    console.error('Erro ao processar Excel:', parseError);
                    liquidFeedback.notify('Erro ao ler o conteúdo do Excel. Verifique o formato.');
                }
            };
            reader.readAsBinaryString(file);
        } catch (err) {
            console.error('Erro ao importar excel:', err);
            liquidFeedback.notify('Erro ao importar Excel.');
        } finally {
            e.target.value = null;
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const L = (await import('xlsx')).default;
            const wb = L.utils.book_new();
            const ws_data = [
                ['Nome', 'Quantidade'],
                ['Exemplo Trabalho 1', 5],
                ['Exemplo Trabalho 2', 10]
            ];
            const ws = L.utils.aoa_to_sheet(ws_data);

            // Ajustar largura das colunas
            ws['!cols'] = [{ wch: 40 }, { wch: 15 }];

            L.utils.book_append_sheet(wb, ws, "Modelo Importação");
            L.writeFile(wb, "modelo_trabalhos.xlsx");
        } catch (err) {
            console.error('Erro ao gerar template:', err);
            liquidFeedback.notify('Erro ao gerar modelo Excel.');
        }
    };

    const handleQuantityChange = (itemId, qty) => {
        const newDetalhes = editData.detalhes_trabalho.map(item =>
            item.id === itemId ? { ...item, quantidade: parseInt(qty) || 0 } : item
        );
        setEditData({ ...editData, detalhes_trabalho: newDetalhes });
    };

    useEffect(() => {
        // Se já houver um mapa e obra mudar, removemos para re-inicializar
        if (mapInstanceRef.current) {
            mapInstanceRef.current.remove();
            mapInstanceRef.current = null;
        }

        if (!obra || !mapRef.current) return;

        const loadMap = async () => {
            try {
                const L = (await import('leaflet')).default;
                await import('leaflet/dist/leaflet.css');

                // Fix icon issues
                delete L.Icon.Default.prototype._getIconUrl;
                L.Icon.Default.mergeOptions({
                    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
                    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
                    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
                });

                const map = L.map(mapRef.current, {
                    center: [obra.latitude, obra.longitude],
                    zoom: 19,
                    scrollWheelZoom: isEditing, // Ativar scroll zoom ao editar para facilitar navegação
                    preferCanvas: true
                });

                L.tileLayer('https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
                    attribution: '&copy; Google Maps',
                    maxZoom: 21,
                    crossOrigin: true
                }).addTo(map);

                // Add main marker
                const currentLat = isEditing ? (editData?.latitude || obra.latitude) : obra.latitude;
                const currentLng = isEditing ? (editData?.longitude || obra.longitude) : obra.longitude;

                const marker = L.marker([currentLat, currentLng], {
                    draggable: isEditing
                })
                    .addTo(map)
                    .bindPopup(`<b>${obra.posicao}</b><br>${obra.cliente}`)
                    .openPopup();

                mainMarkerRef.current = marker;

                if (isEditing) {
                    marker.on('dragend', (e) => {
                        const { lat, lng } = e.target.getLatLng();
                        setEditData(prev => ({ ...prev, latitude: lat, longitude: lng }));
                    });
                }

                // Add drawings if they exist — and track layers for editing
                const existingDrawnLayers = [];
                const existingPolygons = { red: [], yellow: [] };
                const existingIcons = [];

                if (obra.map_data) {
                    const { polygons, icons } = obra.map_data;

                    // Draw polygons
                    if (polygons) {
                        if (polygons.red && Array.isArray(polygons.red)) {
                            polygons.red.forEach(points => {
                                if (points && Array.isArray(points) && points.length >= 3) {
                                    const poly = L.polygon(points, {
                                        color: '#ff0000',
                                        fillColor: '#ff0000',
                                        fillOpacity: 0.3,
                                        weight: 2
                                    }).addTo(map);
                                    existingDrawnLayers.push(poly);
                                    existingPolygons.red.push(points);
                                }
                            });
                        }
                        if (polygons.yellow && Array.isArray(polygons.yellow)) {
                            polygons.yellow.forEach(points => {
                                if (points && Array.isArray(points) && points.length >= 3) {
                                    const poly = L.polygon(points, {
                                        color: '#ffff00',
                                        fillColor: '#ffff00',
                                        fillOpacity: 0.3,
                                        weight: 2
                                    }).addTo(map);
                                    existingDrawnLayers.push(poly);
                                    existingPolygons.yellow.push(points);
                                }
                            });
                        }
                    }

                    // Draw icons
                    if (icons && Array.isArray(icons)) {
                        icons.forEach(icon => {
                            if (!icon || icon.lat === undefined || icon.lng === undefined) return;
                            const iconConfig = {
                                'cone': { emoji: '🚧', bg: '#FF9800' },
                                'health': { emoji: '✚', bg: '#ffffff', color: '#00aa00', border: '2px solid #00aa00' },
                                'extinguisher': { emoji: '🧯', bg: '#F44336' },
                                'start': { emoji: '⚠️', bg: '#FFC107' },
                                'end': { emoji: '🛑', bg: '#F44336' },
                                'warning': { emoji: '⚠️', bg: '#FFC107' }
                            };

                            const config = iconConfig[icon.type] || { emoji: '📍', bg: '#9E9E9E' };

                            const iconMarker = L.marker([icon.lat, icon.lng], {
                                icon: L.divIcon({
                                    html: `<div style="
                                        background: ${config.bg};
                                        color: ${config.color || 'black'};
                                        border: ${config.border || '2px solid white'};
                                        border-radius: 8px;
                                        width: 32px;
                                        height: 32px;
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        font-size: 20px;
                                        box-shadow: 0 2px 5px rgba(0,0,0,0.3);
                                    ">${config.emoji}</div>`,
                                    className: 'custom-map-icon',
                                    iconSize: [32, 32],
                                    iconAnchor: [16, 16]
                                })
                            }).addTo(map);
                            existingDrawnLayers.push(iconMarker);
                            existingIcons.push(icon);
                        });
                    }
                }

                // Se estamos a editar, inicializar o estado mapTools com os dados existentes
                if (isEditing) {
                    setMapTools({
                        activeTool: null,
                        polygons: existingPolygons,
                        icons: existingIcons,
                        drawnLayers: existingDrawnLayers
                    });
                }

                mapInstanceRef.current = map;
            } catch (error) {
                console.error('Error initializing map:', error);
            }
        };

        loadMap();

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, [obra, isEditing]);

    // === Drawing Tools Handlers (only active when editing) ===

    // Handle drawing tools — click and dblclick for polygons and icons
    useEffect(() => {
        if (!mapInstanceRef.current || !isEditing) return;

        const map = mapInstanceRef.current;

        const handleMapClick = async (e) => {
            if (!mapTools.activeTool) {
                // Se não há ferramenta ativa, mover o marcador (comportamento original)
                const { lat, lng } = e.latlng;
                if (mainMarkerRef.current) {
                    mainMarkerRef.current.setLatLng([lat, lng]);
                }
                setEditData(prev => ({ ...prev, latitude: lat, longitude: lng }));
                return;
            }

            const L = (await import('leaflet')).default;
            const { lat, lng } = e.latlng;

            if (mapTools.activeTool.startsWith('polygon')) {
                const color = mapTools.activeTool === 'polygon-red' ? '#ff0000' : '#ffff00';

                if (!window.currentPolygonPoints) {
                    window.currentPolygonPoints = [];
                    window.currentPolygonMarkers = [];
                }

                window.currentPolygonPoints.push([lat, lng]);
                setCurrentPolygonPoints([...window.currentPolygonPoints]);

                // Add temporary marker
                const tempMarker = L.circleMarker([lat, lng], {
                    radius: 4,
                    color: color,
                    fillColor: color,
                    fillOpacity: 1
                }).addTo(map);
                window.currentPolygonMarkers.push(tempMarker);

                // Draw line if we have at least 2 points
                if (window.currentPolygonPoints.length >= 2) {
                    if (window.currentPolyline) {
                        map.removeLayer(window.currentPolyline);
                    }
                    window.currentPolyline = L.polyline(window.currentPolygonPoints, {
                        color: color,
                        weight: 2
                    }).addTo(map);
                }
            } else if (mapTools.activeTool?.startsWith('icon-')) {
                const iconType = mapTools.activeTool.replace('icon-', '');
                const iconConfig = {
                    'cone': { emoji: '🚧', bg: '#FF9800' },
                    'health': { emoji: '✚', bg: '#ffffff', color: '#00aa00', border: '2px solid #00aa00' },
                    'extinguisher': { emoji: '🧯', bg: '#F44336' },
                    'start': { emoji: '⚠️', bg: '#FFC107' },
                    'end': { emoji: '🛑', bg: '#F44336' },
                    'warning': { emoji: '⚠️', bg: '#FFC107' }
                };

                const config = iconConfig[iconType] || { emoji: '📍', bg: '#9E9E9E' };

                const iconMarker = L.marker([lat, lng], {
                    icon: L.divIcon({
                        html: `<div style="
                            background: ${config.bg};
                            color: ${config.color || 'black'};
                            border: ${config.border || '2px solid white'};
                            border-radius: 8px;
                            width: 32px;
                            height: 32px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-size: 20px;
                            box-shadow: 0 2px 5px rgba(0,0,0,0.3);
                        ">${config.emoji}</div>`,
                        className: 'custom-map-icon',
                        iconSize: [32, 32],
                        iconAnchor: [16, 16]
                    })
                }).addTo(map);

                setMapTools(prev => ({
                    ...prev,
                    icons: [...prev.icons, { lat, lng, type: iconType }],
                    drawnLayers: [...prev.drawnLayers, iconMarker]
                }));
            }
        };

        const handleMapDblClick = async (e) => {
            if (mapTools.activeTool?.startsWith('polygon') && window.currentPolygonPoints && window.currentPolygonPoints.length >= 3) {
                const finalPoints = [...window.currentPolygonPoints];
                const L = (await import('leaflet')).default;
                const color = mapTools.activeTool === 'polygon-red' ? '#ff0000' : '#ffff00';
                const polygonType = mapTools.activeTool === 'polygon-red' ? 'red' : 'yellow';

                // Remove temporary markers and polyline
                window.currentPolygonMarkers?.forEach(m => map.removeLayer(m));
                if (window.currentPolyline) map.removeLayer(window.currentPolyline);

                // Create final polygon
                const polygon = L.polygon(finalPoints, {
                    color: color,
                    fillColor: color,
                    fillOpacity: 0.3,
                    weight: 2
                }).addTo(map);

                setMapTools(prev => ({
                    ...prev,
                    polygons: {
                        ...prev.polygons,
                        [polygonType]: [...prev.polygons[polygonType], finalPoints]
                    },
                    drawnLayers: [...prev.drawnLayers, polygon],
                    activeTool: null
                }));

                // Reset drawing state
                window.currentPolygonPoints = null;
                window.currentPolygonMarkers = null;
                window.currentPolyline = null;
                setCurrentPolygonPoints([]);
            }
        };

        map.on('click', handleMapClick);
        map.on('dblclick', handleMapDblClick);

        return () => {
            map.off('click', handleMapClick);
            map.off('dblclick', handleMapDblClick);
        };
    }, [mapTools.activeTool, isEditing]);

    // Clear temporary drawing state when switching tools
    useEffect(() => {
        if (mapInstanceRef.current && isEditing) {
            const map = mapInstanceRef.current;
            if (window.currentPolygonMarkers) {
                window.currentPolygonMarkers.forEach(m => {
                    try { map.removeLayer(m); } catch (e) { }
                });
            }
            if (window.currentPolyline) {
                try { map.removeLayer(window.currentPolyline); } catch (e) { }
            }
            window.currentPolygonPoints = null;
            window.currentPolygonMarkers = null;
            window.currentPolyline = null;
        }
    }, [mapTools.activeTool]);

    // Clear drawn layers when clearing tools
    useEffect(() => {
        if (mapTools.drawnLayers.length === 0 && mapInstanceRef.current && isEditing) {
            const map = mapInstanceRef.current;
            window.currentPolygonMarkers?.forEach(m => map.removeLayer(m));
            if (window.currentPolyline) map.removeLayer(window.currentPolyline);
            window.currentPolygonPoints = null;
            window.currentPolygonMarkers = null;
            window.currentPolyline = null;
        }
    }, [mapTools.drawnLayers.length]);

    // Function to manually finish polygon (alternative to double-click)
    const finishPolygon = async () => {
        if (!window.currentPolygonPoints || window.currentPolygonPoints.length < 3) {
            liquidFeedback.notify('Precisa de pelo menos 3 pontos para criar um polígono!');
            return;
        }

        const finalPoints = [...window.currentPolygonPoints];
        const L = (await import('leaflet')).default;
        const map = mapInstanceRef.current;
        const color = mapTools.activeTool === 'polygon-red' ? '#ff0000' : '#ffff00';
        const polygonType = mapTools.activeTool === 'polygon-red' ? 'red' : 'yellow';

        // Remove temporary markers and polyline
        window.currentPolygonMarkers?.forEach(m => map.removeLayer(m));
        if (window.currentPolyline) map.removeLayer(window.currentPolyline);

        // Create final polygon
        const polygon = L.polygon(finalPoints, {
            color: color,
            fillColor: color,
            fillOpacity: 0.3,
            weight: 2
        }).addTo(map);

        setMapTools(prev => ({
            ...prev,
            polygons: {
                ...prev.polygons,
                [polygonType]: [...prev.polygons[polygonType], finalPoints]
            },
            drawnLayers: [...prev.drawnLayers, polygon],
            activeTool: null
        }));

        // Reset drawing state
        window.currentPolygonPoints = null;
        window.currentPolygonMarkers = null;
        window.currentPolyline = null;
        setCurrentPolygonPoints([]);
    };

    if (loading) return <div className="loading-screen">Carregando detalhes...</div>;
    if (!obra) return <div className="error-message">Obra não encontrada.</div>;

    const getStatusLabel = (status) => {
        const map = {
            'planeada': 'Planeada',
            'em_curso': 'Em Curso',
            'concluida': 'Concluída',
            'suspensa': 'Suspensa'
        };
        return map[status] || status;
    };

    return (
        <div className="obra-detalhes-wrapper">
            {/* Cabeçalho Uniforme - Agora fora do contentor com max-width */}
            <div className="top-bar">
                <div className="top-bar-left">
                    <button className="btn-back" onClick={() => navigate('/obras')}>
                        ← Voltar
                    </button>
                    <div className="top-bar-title">
                        {isEditing ? (
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <input
                                    type="text"
                                    value={editData.posicao}
                                    onChange={e => setEditData({ ...editData, posicao: e.target.value })}
                                    style={{ width: '100px', fontWeight: 'bold' }}
                                    placeholder="Posição"
                                />
                                <span style={{ color: '#cbd5e0' }}>-</span>
                                <input
                                    type="text"
                                    value={editData.descricao}
                                    onChange={e => setEditData({ ...editData, descricao: e.target.value })}
                                    style={{ width: '300px' }}
                                    placeholder="Descrição da Obra"
                                />
                                <select
                                    value={editData.status}
                                    onChange={e => setEditData({ ...editData, status: e.target.value })}
                                    className={`status-badge ${editData.status}`}
                                    style={{ border: 'none', cursor: 'pointer', height: '36px' }}
                                >
                                    <option value="planeada">Planeada</option>
                                    <option value="em_curso">Em Curso</option>
                                    <option value="concluida">Concluída</option>
                                    <option value="suspensa">Suspensa</option>
                                </select>
                                <select
                                    value={editData.tipo_obra || 'obra'}
                                    onChange={e => setEditData({ ...editData, tipo_obra: e.target.value })}
                                    style={{ height: '36px', borderRadius: '6px', border: '1px solid #cbd5e0', cursor: 'pointer' }}
                                    title="Tipo: obra ou manutenção"
                                >
                                    <option value="obra">Obra</option>
                                    <option value="manutencao">Manutenção</option>
                                    <option value="pendencia">Pendência</option>
                                </select>
                            </div>
                        ) : (
                            <>
                                <h1>
                                    {obra.posicao} - {obra.descricao || obra.cliente}
                                    <span className={`status-badge ${obra.status}`} style={{ fontSize: '12px', verticalAlign: 'middle', marginLeft: '12px' }}>
                                        {getStatusLabel(obra.status)}
                                    </span>
                                    {obra.email_seguranca_enviado && (
                                        <span className="status-badge" style={{ fontSize: '11px', verticalAlign: 'middle', marginLeft: '8px', backgroundColor: '#38a169', color: 'white' }}>
                                            <OrcamentoIcon name="mail" fallback="✉️" /> Email Enviado
                                        </span>
                                    )}
                                </h1>
                                <p>Detalhes completos e gestão da obra</p>
                            </>
                        )}
                    </div>
                </div>

                <div className="top-bar-actions">
                    <button
                        className="btn-secondary"
                        onClick={handleFixMap}
                        disabled={savingMap}
                        title="Fixar a vista atual do mapa para os relatórios PDF"
                    >
                        <OrcamentoIcon name={savingMap ? "clock" : "pin"} fallback={savingMap ? "⏳" : "📌"} /> {savingMap ? 'A fixar...' : 'Fixar Mapa'}
                    </button>
                    <button
                        className="btn-primary"
                        style={{ backgroundColor: '#805ad5' }}
                        onClick={handleOpenMaqueteEditor}
                    >
                        <OrcamentoIcon name="layers" fallback="🎨" /> Maquete
                    </button>
                    <button
                        className="btn-primary"
                        onClick={handleGeneratePDF}
                        disabled={generatingDetailed}
                    >
                        <OrcamentoIcon name={generatingDetailed ? "clock" : "document"} fallback={generatingDetailed ? "⏳" : "📄"} /> {generatingDetailed ? 'A gerar...' : 'Gerar PDF'}
                    </button>
                    <button
                        className="btn-primary"
                        style={{ backgroundColor: '#ed8936' }}
                        onClick={handleSendEmail}
                        disabled={sendingEmail}
                        title="Enviar PDF por email para o Responsável de Segurança"
                    >
                        <OrcamentoIcon name={sendingEmail ? "clock" : "mail"} fallback={sendingEmail ? "⏳" : "✉️"} /> {sendingEmail ? 'A enviar...' : 'Enviar Email'}
                    </button>

                    {isEditing ? (
                        <button className="btn-primary" style={{ backgroundColor: '#38a169' }} onClick={handleSaveEdit} disabled={isSaving}>
                            <OrcamentoIcon name={isSaving ? "clock" : "save"} fallback={isSaving ? "⏳" : "💾"} /> {isSaving ? 'A gravar...' : 'Gravar'}
                        </button>
                    ) : (
                        <>
                            <button className="btn-primary" style={{ backgroundColor: '#3182ce' }} onClick={() => setIsEditing(true)}>
                                <OrcamentoIcon name="edit" fallback="✏️" /> Editar
                            </button>
                            <button
                                className="btn-danger"
                                style={{
                                    backgroundColor: '#e53e3e',
                                    color: 'white',
                                    border: 'none',
                                    padding: '8px 14px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: 'bold'
                                }}
                                onClick={handleDeleteObra}
                                disabled={isDeleting}
                            >
                                <OrcamentoIcon name="trash" fallback="🗑️" /> {isDeleting ? 'A apagar...' : 'Apagar'}
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="page-content">
                <div className="obra-detalhes-page">

                    {showMaqueteModal && (
                        <LiquidDialog onClose={() => setShowMaqueteModal(false)} className="modal-overlay">
                            <div className="modal-content">
                                <h3>Gerar Maquete</h3>
                                <div className="form-group">
                                    <label>Selecione o Template:</label>
                                    <select
                                        value={selectedTemplateForGen}
                                        onChange={e => setSelectedTemplateForGen(e.target.value)}
                                        style={{ width: '100%', padding: '8px', marginTop: '5px' }}
                                    >
                                        {templates.map(t => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="modal-actions" style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                    <button onClick={() => setShowMaqueteModal(false)} className="btn-secondary">Cancelar</button>
                                    <button onClick={handleGenerateMaquete} className="btn-primary">Gerar PDF</button>
                                </div>
                            </div>
                        </LiquidDialog>
                    )}

                    <div className="obra-content-grid">
                        <div className="info-column">
                            <div className="info-card">
                                <h2><OrcamentoIcon name="document" fallback="📋" /> Dados Gerais</h2>

                                <div className="detail-row">
                                    <label>Cliente</label>
                                    {isEditing ? (
                                        <select
                                            value={editData.cliente_id || ''}
                                            onChange={e => {
                                                const c = clientes.find(cl => cl.id === parseInt(e.target.value));
                                                setEditData({ ...editData, cliente_id: e.target.value, cliente: c ? c.nome : '' });
                                            }}
                                            className="form-control"
                                        >
                                            <option value="">-- Selecionar cliente --</option>
                                            {clientes.map(c => (
                                                <option key={c.id} value={c.id}>{c.nome}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span>{obra.cliente}</span>
                                    )}
                                </div>

                                <div className="detail-row">
                                    <label>Datas Previstas</label>
                                    {isEditing ? (
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                                            <input
                                                type="date"
                                                value={editData.data_inicio}
                                                onChange={e => setEditData({ ...editData, data_inicio: e.target.value })}
                                                className="form-control"
                                                style={{ flex: 1, minWidth: 0, fontSize: '14px', padding: '8px 12px' }}
                                            />
                                            <span style={{ fontSize: '14px', color: '#4a5568', flexShrink: 0 }}>até</span>
                                            <input
                                                type="date"
                                                value={editData.data_fim}
                                                onChange={e => setEditData({ ...editData, data_fim: e.target.value })}
                                                className="form-control"
                                                style={{ flex: 1, minWidth: 0, fontSize: '14px', padding: '8px 12px' }}
                                            />
                                        </div>
                                    ) : (
                                        <span>
                                            {obra.data_inicio && obra.data_fim ? (
                                                `${new Date(obra.data_inicio).toLocaleDateString('pt-PT')} a ${new Date(obra.data_fim).toLocaleDateString('pt-PT')}`
                                            ) : (
                                                'Datas não definidas'
                                            )}
                                        </span>
                                    )}
                                </div>

                                {obra.descricao && (
                                    <div className="detail-row">
                                        <label>Descrição</label>
                                        <span>{obra.descricao}</span>
                                    </div>
                                )}
                            </div>

                        </div>

                        {/* Observações agora ficam aqui na coluna esquerda */}
                        <div className="info-card" style={{ marginTop: '20px' }}>
                            <h2><OrcamentoIcon name="document" fallback="📝" /> Observações</h2>
                            {isEditing ? (
                                <textarea
                                    className="form-control"
                                    value={editData.observacoes}
                                    onChange={(e) => setEditData({ ...editData, observacoes: e.target.value })}
                                    placeholder="Insira observações relevantes para esta obra..."
                                    style={{ width: '100%', minHeight: '120px', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e0', fontSize: '14px', fontFamily: 'inherit' }}
                                />
                            ) : (
                                <div data-liquid-surface="true" style={{ whiteSpace: 'pre-wrap', color: '#4a5568', padding: '10px', backgroundColor: '#f9fafb', borderRadius: '8px', minHeight: '60px', fontSize: '14px' }}>
                                    {obra.observacoes || 'Sem observações registadas.'}
                                </div>
                            )}
                        </div>

                        {/* Detalhes de Relatório removidos daqui - vão para baixo */}
                    </div>

                    <div className="map-column">
                        <div className="info-card map-card">
                            <h2><OrcamentoIcon name="map" fallback="🗺️" /> Localização e Implantação</h2>
                            {isEditing && (
                                <div style={{ marginBottom: '15px' }}>
                                    <MapSearch onLocationSelect={handleLocationSelect} />
                                </div>
                            )}

                            {/* Drawing Tools — visible only when editing */}
                            {isEditing && (
                                <>
                                    <div data-liquid-surface="true" className="map-tools" style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '6px',
                                        marginBottom: '10px',
                                        padding: '8px',
                                        background: '#f7fafc',
                                        borderRadius: '8px',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        <button
                                            type="button"
                                            className={`tool-btn ${mapTools.activeTool === 'polygon-red' ? 'active' : ''}`}
                                            onClick={() => setMapTools(prev => ({ ...prev, activeTool: prev.activeTool === 'polygon-red' ? null : 'polygon-red' }))}
                                            title="Desenhar área vermelha"
                                            style={{
                                                padding: '6px 10px',
                                                fontSize: '12px',
                                                border: mapTools.activeTool === 'polygon-red' ? '2px solid #e53e3e' : '1px solid #cbd5e0',
                                                borderRadius: '6px',
                                                background: mapTools.activeTool === 'polygon-red' ? '#fff5f5' : 'white',
                                                cursor: 'pointer',
                                                fontWeight: mapTools.activeTool === 'polygon-red' ? '600' : '400'
                                            }}
                                        ><LiquidText>
                                            🔴 Polígono Vermelho
                                        </LiquidText></button>
                                        <button
                                            type="button"
                                            className={`tool-btn ${mapTools.activeTool === 'polygon-yellow' ? 'active' : ''}`}
                                            onClick={() => setMapTools(prev => ({ ...prev, activeTool: prev.activeTool === 'polygon-yellow' ? null : 'polygon-yellow' }))}
                                            title="Desenhar área amarela"
                                            style={{
                                                padding: '6px 10px',
                                                fontSize: '12px',
                                                border: mapTools.activeTool === 'polygon-yellow' ? '2px solid #d69e2e' : '1px solid #cbd5e0',
                                                borderRadius: '6px',
                                                background: mapTools.activeTool === 'polygon-yellow' ? '#fffff0' : 'white',
                                                cursor: 'pointer',
                                                fontWeight: mapTools.activeTool === 'polygon-yellow' ? '600' : '400'
                                            }}
                                        ><LiquidText>
                                            🟡 Polígono Amarelo
                                        </LiquidText></button>
                                        <button
                                            type="button"
                                            className={`tool-btn ${mapTools.activeTool === 'icon-cone' ? 'active' : ''}`}
                                            onClick={() => setMapTools(prev => ({ ...prev, activeTool: prev.activeTool === 'icon-cone' ? null : 'icon-cone' }))}
                                            title="Colocar cone"
                                            style={{
                                                padding: '6px 10px',
                                                fontSize: '12px',
                                                border: mapTools.activeTool === 'icon-cone' ? '2px solid #dd6b20' : '1px solid #cbd5e0',
                                                borderRadius: '6px',
                                                background: mapTools.activeTool === 'icon-cone' ? '#fffaf0' : 'white',
                                                cursor: 'pointer',
                                                fontWeight: mapTools.activeTool === 'icon-cone' ? '600' : '400'
                                            }}
                                        ><LiquidText>
                                            🚧 Cone
                                        </LiquidText></button>
                                        <button
                                            type="button"
                                            className={`tool-btn ${mapTools.activeTool === 'icon-health' ? 'active' : ''}`}
                                            onClick={() => setMapTools(prev => ({ ...prev, activeTool: prev.activeTool === 'icon-health' ? null : 'icon-health' }))}
                                            title="Cruz Saúde"
                                            style={{
                                                padding: '6px 10px',
                                                fontSize: '12px',
                                                border: mapTools.activeTool === 'icon-health' ? '2px solid #38a169' : '1px solid #cbd5e0',
                                                borderRadius: '6px',
                                                background: mapTools.activeTool === 'icon-health' ? '#f0fff4' : 'white',
                                                cursor: 'pointer',
                                                fontWeight: mapTools.activeTool === 'icon-health' ? '600' : '400'
                                            }}
                                        ><LiquidText>
                                            ➕ Saúde
                                        </LiquidText></button>
                                        <button
                                            type="button"
                                            className={`tool-btn ${mapTools.activeTool === 'icon-extinguisher' ? 'active' : ''}`}
                                            onClick={() => setMapTools(prev => ({ ...prev, activeTool: prev.activeTool === 'icon-extinguisher' ? null : 'icon-extinguisher' }))}
                                            title="Extintor"
                                            style={{
                                                padding: '6px 10px',
                                                fontSize: '12px',
                                                border: mapTools.activeTool === 'icon-extinguisher' ? '2px solid #e53e3e' : '1px solid #cbd5e0',
                                                borderRadius: '6px',
                                                background: mapTools.activeTool === 'icon-extinguisher' ? '#fff5f5' : 'white',
                                                cursor: 'pointer',
                                                fontWeight: mapTools.activeTool === 'icon-extinguisher' ? '600' : '400'
                                            }}
                                        ><LiquidText>
                                            🧯 Extintor
                                        </LiquidText></button>
                                        <button
                                            type="button"
                                            className={`tool-btn ${mapTools.activeTool === 'icon-start' ? 'active' : ''}`}
                                            onClick={() => setMapTools(prev => ({ ...prev, activeTool: prev.activeTool === 'icon-start' ? null : 'icon-start' }))}
                                            title="Local a Intervencionar"
                                            style={{
                                                padding: '6px 10px',
                                                fontSize: '12px',
                                                border: mapTools.activeTool === 'icon-start' ? '2px solid #d69e2e' : '1px solid #cbd5e0',
                                                borderRadius: '6px',
                                                background: mapTools.activeTool === 'icon-start' ? '#fffff0' : 'white',
                                                cursor: 'pointer',
                                                fontWeight: mapTools.activeTool === 'icon-start' ? '600' : '400'
                                            }}
                                        >
                                            <OrcamentoIcon name="warning" fallback="⚠️" /> Local Interv.
                                        </button>
                                        <button
                                            type="button"
                                            className={`tool-btn ${mapTools.activeTool === 'icon-end' ? 'active' : ''}`}
                                            onClick={() => setMapTools(prev => ({ ...prev, activeTool: prev.activeTool === 'icon-end' ? null : 'icon-end' }))}
                                            title="Fim de Obra"
                                            style={{
                                                padding: '6px 10px',
                                                fontSize: '12px',
                                                border: mapTools.activeTool === 'icon-end' ? '2px solid #e53e3e' : '1px solid #cbd5e0',
                                                borderRadius: '6px',
                                                background: mapTools.activeTool === 'icon-end' ? '#fff5f5' : 'white',
                                                cursor: 'pointer',
                                                fontWeight: mapTools.activeTool === 'icon-end' ? '600' : '400'
                                            }}
                                        ><LiquidText>
                                            🛑 Fim
                                        </LiquidText></button>
                                        <button
                                            type="button"
                                            className={`tool-btn ${mapTools.activeTool === 'icon-warning' ? 'active' : ''}`}
                                            onClick={() => setMapTools(prev => ({ ...prev, activeTool: prev.activeTool === 'icon-warning' ? null : 'icon-warning' }))}
                                            title="Sinalização"
                                            style={{
                                                padding: '6px 10px',
                                                fontSize: '12px',
                                                border: mapTools.activeTool === 'icon-warning' ? '2px solid #d69e2e' : '1px solid #cbd5e0',
                                                borderRadius: '6px',
                                                background: mapTools.activeTool === 'icon-warning' ? '#fffff0' : 'white',
                                                cursor: 'pointer',
                                                fontWeight: mapTools.activeTool === 'icon-warning' ? '600' : '400'
                                            }}
                                        >
                                            <OrcamentoIcon name="warning" fallback="⚠️" /> Sinalização
                                        </button>
                                        <button
                                            type="button"
                                            style={{
                                                padding: '6px 10px',
                                                fontSize: '12px',
                                                border: '1px solid #e53e3e',
                                                borderRadius: '6px',
                                                background: '#fff5f5',
                                                color: '#e53e3e',
                                                cursor: 'pointer',
                                                fontWeight: '500'
                                            }}
                                            onClick={() => {
                                                // Remover todas as camadas desenhadas do mapa
                                                const map = mapInstanceRef.current;
                                                if (map) {
                                                    mapTools.drawnLayers.forEach(layer => {
                                                        try { map.removeLayer(layer); } catch (e) { }
                                                    });
                                                }
                                                setMapTools({ activeTool: null, polygons: { red: [], yellow: [] }, icons: [], drawnLayers: [] });
                                            }}
                                            title="Limpar todos os desenhos"
                                        >
                                            <OrcamentoIcon name="trash" fallback="🗑️" /> Limpar Tudo
                                        </button>
                                    </div>

                                    {/* Drawing Instructions */}
                                    {mapTools.activeTool?.startsWith('polygon') && (
                                        <div style={{
                                            padding: '8px 12px',
                                            background: '#E3F2FD',
                                            border: '1px solid #2196F3',
                                            borderRadius: '4px',
                                            marginBottom: '10px',
                                            fontSize: '13px',
                                            color: '#1976D2',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}>
                                            <span><LiquidText>
                                                📌 Clique no mapa para adicionar pontos
                                                </LiquidText>{currentPolygonPoints.length > 0 && (
                                                    <strong> ({currentPolygonPoints.length} pontos)</strong>
                                                )}
                                            </span>
                                            {currentPolygonPoints.length >= 3 && (
                                                <button
                                                    type="button"
                                                    onClick={finishPolygon}
                                                    style={{
                                                        background: '#4CAF50',
                                                        color: 'white',
                                                        border: 'none',
                                                        padding: '6px 16px',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontWeight: '600',
                                                        fontSize: '12px'
                                                    }}
                                                ><LiquidText>
                                                    ✓ Finalizar Polígono
                                                </LiquidText></button>
                                            )}
                                        </div>
                                    )}

                                    {mapTools.activeTool?.startsWith('icon-') && (
                                        <div style={{
                                            padding: '8px 12px',
                                            background: '#FFF3E0',
                                            border: '1px solid #FF9800',
                                            borderRadius: '4px',
                                            marginBottom: '10px',
                                            fontSize: '13px',
                                            color: '#E65100'
                                        }}><LiquidText>
                                            📌 Clique no mapa para colocar o ícone
                                        </LiquidText></div>
                                    )}
                                </>
                            )}

                            <div ref={mapRef} className="map-view"></div>
                            <div style={{ marginTop: '10px', fontSize: '13px', color: '#4a5568' }}>
                                {isEditing ? (
                                    <div style={{ display: 'flex', gap: '15px' }}>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '11px', color: '#718096' }}>Latitude</label>
                                            <input
                                                type="number"
                                                step="0.00000001"
                                                value={editData.latitude}
                                                onChange={e => setEditData({ ...editData, latitude: parseFloat(e.target.value) || 0 })}
                                                className="form-control"
                                                style={{ height: '32px', fontSize: '13px' }}
                                            />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <label style={{ display: 'block', marginBottom: '4px', fontSize: '11px', color: '#718096' }}>Longitude</label>
                                            <input
                                                type="number"
                                                step="0.00000001"
                                                value={editData.longitude}
                                                onChange={e => setEditData({ ...editData, longitude: parseFloat(e.target.value) || 0 })}
                                                className="form-control"
                                                style={{ height: '32px', fontSize: '13px' }}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <>📍 Coordenadas: {obra.latitude}, {obra.longitude}</>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Trabalhos Previstos - Agora full-width embaixo */}
                <div className="info-card" style={{ marginTop: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h2 style={{ margin: 0 }}><OrcamentoIcon name="maintenance" fallback="🔨" /> Trabalhos Previstos</h2>
                        {isEditing && (
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={handleDownloadTemplate}
                                    style={{ margin: 0, fontSize: '12px', padding: '5px 10px', display: 'flex', alignItems: 'center', gap: '5px' }}
                                >
                                    <OrcamentoIcon name="download" fallback="📥" /> Template Excel
                                </button>
                                <label className="btn-secondary" style={{ cursor: 'pointer', margin: 0, fontSize: '12px', padding: '5px 10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <OrcamentoIcon name="upload" fallback="📤" /> Importar Excel
                                    <input type="file" accept=".xlsx, .xls" onChange={handleImportProjectExcel} style={{ display: 'none' }} />
                                </label>
                            </div>
                        )}
                    </div>

                    {isEditing ? (
                        <div className="work-items-manager">
                            {/* Add New Item Control */}
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', padding: '10px', backgroundColor: '#f0f4f8', borderRadius: '8px' }}>
                                <select
                                    className="form-control"
                                    value={selectedWorkItemToAdd}
                                    onChange={e => setSelectedWorkItemToAdd(e.target.value)}
                                    style={{ flex: 1 }}
                                >
                                    <option value="">Selecione um trabalho para adicionar...</option>
                                    {availableWorkItems.map(item => (
                                        <option key={item.id} value={item.id}>
                                            {item.nome}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    className="btn-primary"
                                    onClick={handleAddWorkItem}
                                    disabled={!selectedWorkItemToAdd}
                                    style={{ backgroundColor: '#48bb78', padding: '0 20px' }}
                                >
                                    + Adicionar
                                </button>
                            </div>

                            {/* List of Selected Items */}
                            <div className="selected-work-items-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {editData.detalhes_trabalho.length === 0 ? (
                                    <div style={{ padding: '20px', textAlign: 'center', color: '#718096', border: '2px dashed #e2e8f0', borderRadius: '8px' }}>
                                        Nenhum trabalho selecionado. Adicione acima ou importe do Excel.
                                    </div>
                                ) : (
                                    editData.detalhes_trabalho.map((detail, index) => (
                                        <div data-liquid-surface="true"
                                            key={detail.id || index}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '10px 15px',
                                                backgroundColor: 'white',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '6px',
                                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <span style={{ fontSize: '18px' }}><LiquidText>{detail.icone || '🔹'}</LiquidText></span>
                                                <span style={{ fontWeight: '500' }}>{detail.nome}</span>
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    <label style={{ fontSize: '12px', color: '#718096' }}>Qtd:</label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={detail.quantidade || 1}
                                                        onChange={(e) => {
                                                            const newQty = parseInt(e.target.value) || 1;
                                                            setEditData(prev => ({
                                                                ...prev,
                                                                detalhes_trabalho: prev.detalhes_trabalho.map(d =>
                                                                    d.id === detail.id ? { ...d, quantidade: newQty } : d
                                                                )
                                                            }));
                                                        }}
                                                        style={{ width: '60px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e0' }}
                                                    />
                                                </div>
                                                <button
                                                    onClick={() => handleRemoveWorkItem(detail.id)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        color: '#e53e3e',
                                                        fontSize: '16px',
                                                        padding: '4px'
                                                    }}
                                                    title="Remover trabalho"
                                                >
                                                    <OrcamentoIcon name="trash" fallback="🗑️" />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="work-tags">
                            {obra.detalhes_trabalho && obra.detalhes_trabalho.length > 0 ? (
                                obra.detalhes_trabalho.map((item, idx) => (
                                    <span key={idx} className="work-tag">
                                        <LiquidText>{item.icone}</LiquidText> {item.nome} {` (x${item.quantidade || 1})`}
                                    </span>
                                ))
                            ) : (
                                Array.isArray(obra.tipo_trabalho) ? obra.tipo_trabalho.map((t, i) => (
                                    <span key={i} className="work-tag">{t}</span>
                                )) : <span className="text-gray-500">Nenhum trabalho especificado</span>
                            )}
                        </div>
                    )}
                </div>

                {/* Detalhes de Relatório - full-width embaixo */}
                {/* Detalhes de Relatório - full-width embaixo */}
                <div className="info-card" style={{ marginTop: '20px' }}>
                    <h2><OrcamentoIcon name="document" fallback="📋" /> Detalhes Obra</h2>
                    <div className="obra-contact-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '15px', alignItems: 'end' }}>
                        {/* Coluna 1: Responsável Obra (Cliente) */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', marginBottom: '0' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}>Responsável Obra (Cliente)</label>
                                {isEditing ? (
                                    <select
                                        value={editData.responsavel_cliente_id}
                                        onChange={e => {
                                            const userId = e.target.value;
                                            const user = users.find(u => u.id === parseInt(userId));
                                            setEditData({
                                                ...editData,
                                                responsavel_cliente_id: userId,
                                                email_responsavel_cliente: user ? user.email : '',
                                                telefone_responsavel_cliente: user ? (user.telefone || '') : ''
                                            });
                                        }}
                                        className="form-control"
                                        style={{ height: '35px', padding: '5px' }}
                                    >
                                        <option value="">Selecione...</option>
                                        {users.filter(u => u.tipo_utilizador === 'externo').map(u => (
                                            <option key={u.id} value={u.id}>{u.nome_completo}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <span style={{ padding: '5px 0' }}>
                                        {users.find(u => u.id === obra.responsavel_cliente_id)?.nome_completo || '-'}
                                    </span>
                                )}
                            </div>
                            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', marginBottom: '0' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}>Email</label>
                                {isEditing ? (
                                    <input
                                        type="email"
                                        value={editData.email_responsavel_cliente}
                                        readOnly
                                        className="form-control"
                                        style={{ height: '35px', padding: '5px', backgroundColor: '#edf2f7', cursor: 'not-allowed' }}
                                    />
                                ) : (
                                    <span style={{ padding: '5px 0', fontSize: '13px' }}>{users.find(u => u.id === obra.responsavel_cliente_id)?.email || '-'}</span>
                                )}
                            </div>
                            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', marginBottom: '0' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}>Telefone</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={editData.telefone_responsavel_cliente}
                                        readOnly
                                        className="form-control"
                                        style={{ height: '35px', padding: '5px', backgroundColor: '#edf2f7', cursor: 'not-allowed' }}
                                    />
                                ) : (
                                    <span style={{ padding: '5px 0', fontSize: '13px' }}>{users.find(u => u.id === obra.responsavel_cliente_id)?.telefone || '-'}</span>
                                )}
                            </div>
                        </div>

                        {/* Coluna 2: Responsável Obra (Interno) */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', marginBottom: '0' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}>Responsável Obra (Interno)</label>
                                {isEditing ? (
                                    <select
                                        value={editData.responsavel_obra_id}
                                        onChange={e => {
                                            const userId = e.target.value;
                                            const user = users.find(u => u.id === parseInt(userId));
                                            setEditData({
                                                ...editData,
                                                responsavel_obra_id: userId,
                                                email_responsavel: user ? user.email : '',
                                                telefone_responsavel: user ? (user.telefone || '') : ''
                                            });
                                        }}
                                        className="form-control"
                                        style={{ height: '35px', padding: '5px' }}
                                    >
                                        <option value="">Selecione Utilizador...</option>
                                        {users.map(u => (
                                            <option key={u.id} value={u.id}>{u.nome_completo}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <span style={{ padding: '5px 0' }}>
                                        {users.find(u => u.id === obra.responsavel_obra_id)?.nome_completo || '-'}
                                    </span>
                                )}
                            </div>
                            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', marginBottom: '0' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}>Email</label>
                                {isEditing ? (
                                    <input
                                        type="email"
                                        value={editData.email_responsavel}
                                        readOnly
                                        className="form-control"
                                        style={{ height: '35px', padding: '5px', backgroundColor: '#edf2f7', cursor: 'not-allowed' }}
                                    />
                                ) : (
                                    <span style={{ padding: '5px 0', fontSize: '13px' }}>{users.find(u => u.id === obra.responsavel_obra_id)?.email || '-'}</span>
                                )}
                            </div>
                            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', marginBottom: '0' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}>Telefone</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={editData.telefone_responsavel}
                                        readOnly
                                        className="form-control"
                                        style={{ height: '35px', padding: '5px', backgroundColor: '#edf2f7', cursor: 'not-allowed' }}
                                    />
                                ) : (
                                    <span style={{ padding: '5px 0', fontSize: '13px' }}>{users.find(u => u.id === obra.responsavel_obra_id)?.telefone || '-'}</span>
                                )}
                            </div>
                        </div>

                        {/* Coluna 3: Segurança Obra */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', marginBottom: '0' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}>Segurança Obra</label>
                                {isEditing ? (
                                    <select
                                        value={editData.seguranca_obra_id}
                                        onChange={e => {
                                            const userId = e.target.value;
                                            const user = users.find(u => u.id === parseInt(userId));
                                            setEditData({
                                                ...editData,
                                                seguranca_obra_id: userId,
                                                email_seguranca: user ? user.email : '',
                                                telefone_seguranca: user ? (user.telefone || '') : ''
                                            });
                                        }}
                                        className="form-control"
                                        style={{ height: '35px', padding: '5px' }}
                                    >
                                        <option value="">Selecione Utilizador...</option>
                                        {users.map(u => (
                                            <option key={u.id} value={u.id}>{u.nome_completo}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <span style={{ padding: '5px 0' }}>
                                        {users.find(u => u.id === obra.seguranca_obra_id)?.nome_completo || '-'}
                                    </span>
                                )}
                            </div>
                            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', marginBottom: '0' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}>Email</label>
                                {isEditing ? (
                                    <input
                                        type="email"
                                        value={editData.email_seguranca}
                                        readOnly
                                        className="form-control"
                                        style={{ height: '35px', padding: '5px', backgroundColor: '#edf2f7', cursor: 'not-allowed' }}
                                    />
                                ) : (
                                    <span style={{ padding: '5px 0', fontSize: '13px' }}>{users.find(u => u.id === obra.seguranca_obra_id)?.email || '-'}</span>
                                )}
                            </div>
                            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', marginBottom: '0' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}>Telefone</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={editData.telefone_seguranca}
                                        readOnly
                                        className="form-control"
                                        style={{ height: '35px', padding: '5px', backgroundColor: '#edf2f7', cursor: 'not-allowed' }}
                                    />
                                ) : (
                                    <span style={{ padding: '5px 0', fontSize: '13px' }}>{users.find(u => u.id === obra.seguranca_obra_id)?.telefone || '-'}</span>
                                )}
                            </div>
                        </div>

                        {/* Coluna 4: Fiscalização */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', marginBottom: '0' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '2px', display: 'block' }}>Fiscalização (Nome)</label>
                                {isEditing ? (
                                    <select
                                        value={editData.fiscalizacao_id}
                                        onChange={e => {
                                            const userId = e.target.value;
                                            const user = users.find(u => u.id === parseInt(userId));
                                            setEditData({
                                                ...editData,
                                                fiscalizacao_id: userId,
                                                nome_fiscalizacao: user ? user.nome_completo : '',
                                                email_fiscalizacao: user ? user.email : '',
                                                telefone_fiscalizacao: user ? (user.telefone || '') : ''
                                            });
                                        }}
                                        className="form-control"
                                        style={{ height: '35px', padding: '5px' }}
                                    >
                                        <option value="">Selecione Fiscal...</option>
                                        {users.map(u => (
                                            <option key={u.id} value={u.id}>{u.nome_completo}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <span style={{ padding: '5px 0' }}>{obra.nome_fiscalizacao || '-'}</span>
                                )}
                            </div>
                            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', marginBottom: '0' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '2px', display: 'block' }}>Email</label>
                                {isEditing ? (
                                    <input
                                        type="email"
                                        value={editData.email_fiscalizacao}
                                        readOnly
                                        className="form-control"
                                        style={{ height: '35px', padding: '5px', backgroundColor: '#edf2f7', cursor: 'not-allowed' }}
                                    />
                                ) : (
                                    <span style={{ padding: '5px 0', fontSize: '13px' }}>{obra.email_fiscalizacao || '-'}</span>
                                )}
                            </div>
                            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', marginBottom: '0' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '2px', display: 'block' }}>Telefone</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={editData.telefone_fiscalizacao}
                                        readOnly
                                        className="form-control"
                                        style={{ height: '35px', padding: '5px', backgroundColor: '#edf2f7', cursor: 'not-allowed' }}
                                    />
                                ) : (
                                    <span style={{ padding: '5px 0', fontSize: '13px' }}>{obra.telefone_fiscalizacao || '-'}</span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
                        {/* Linha Horizontal com 4 campos */}
                        {/* Linha Horizontal com 4 campos */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', width: '100%', marginTop: '5px' }}>
                            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}>Doc. Validada?</label>
                                {isEditing ? (
                                    <select
                                        value={editData.documentacao_validada}
                                        onChange={e => setEditData({ ...editData, documentacao_validada: e.target.value })}
                                        className="form-control"
                                        style={{ height: '35px', padding: '5px' }}
                                    >
                                        <option value="">Selecione...</option>
                                        <option value="s">Sim</option>
                                        <option value="n">Não</option>
                                    </select>
                                ) : (
                                    <span>{obra.documentacao_validada === 's' ? 'Sim' : (obra.documentacao_validada === 'n' ? 'Não' : '-')}</span>
                                )}
                            </div>
                            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}>Necessidade de AT?</label>
                                {isEditing ? (
                                    <select
                                        value={editData.necessidade_at}
                                        onChange={e => setEditData({ ...editData, necessidade_at: e.target.value })}
                                        className="form-control"
                                        style={{ height: '35px', padding: '5px' }}
                                    >
                                        <option value="">Selecione...</option>
                                        <option value="s">Sim</option>
                                        <option value="n">Não</option>
                                    </select>
                                ) : (
                                    <span>{obra.necessidade_at === 's' ? 'Sim' : (obra.necessidade_at === 'n' ? 'Não' : '-')}</span>
                                )}
                            </div>
                            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}>Data Trabalho AT</label>
                                {isEditing ? (
                                    <input
                                        type="date"
                                        value={editData.data_at}
                                        onChange={e => setEditData({ ...editData, data_at: e.target.value })}
                                        className="form-control"
                                        style={{ height: '35px', padding: '5px' }}
                                    />
                                ) : (
                                    <span>{obra.data_at ? new Date(obra.data_at).toLocaleDateString('pt-PT') : '-'}</span>
                                )}
                            </div>
                            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}>Hora Emissão AT</label>
                                {isEditing ? (
                                    <input
                                        type="time"
                                        value={editData.hora_at}
                                        onChange={e => setEditData({ ...editData, hora_at: e.target.value })}
                                        className="form-control"
                                        style={{ height: '35px', padding: '5px' }}
                                    />
                                ) : (
                                    <span>{obra.hora_at || '-'}</span>
                                )}
                            </div>
                            <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '2px' }}>Email Segurança?</label>
                                {isEditing ? (
                                    <select
                                        value={editData.email_seguranca_enviado ? 's' : 'n'}
                                        onChange={e => setEditData({ ...editData, email_seguranca_enviado: e.target.value === 's' })}
                                        className="form-control"
                                        style={{ height: '35px', padding: '5px' }}
                                    >
                                        <option value="s">Enviado</option>
                                        <option value="n">Não Enviado</option>
                                    </select>
                                ) : (
                                    <span className={`obra-email-status ${obra.email_seguranca_enviado ? 'sent' : 'pending'}`} style={{ color: obra.email_seguranca_enviado ? '#38a169' : '#e53e3e', fontWeight: 'bold' }}>
                                        <OrcamentoIcon name={obra.email_seguranca_enviado ? "check" : "mail"} fallback={obra.email_seguranca_enviado ? "✅" : "❌"} /> {obra.email_seguranca_enviado ? 'Enviado' : 'Não Enviado'}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <ObraRecursos obraId={id} />
                <FichasLevantamentoLista obraId={id} />
            </div>
        </div>
    );
};

export default ObraDetalhes;
